import { Link } from "react-router-dom";
import { ArrowUpRight, Download } from "lucide-react";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { executive, philosophy, articles, CANONICAL_ORIGIN, LEADERSHIP_PATH } from "@/data/site";

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: { "@id": `${CANONICAL_ORIGIN}${LEADERSHIP_PATH}#adeshi-moffi` },
    url: `${CANONICAL_ORIGIN}${LEADERSHIP_PATH}`,
    name: `${executive.name} — ${executive.title}`,
    description: executive.bio[0],
  };

  return (
    <>
      <Seo
        title={`Adeshi Moffi | ${executive.title}, Investors Pathway LLC`}
        description="Adeshi Moffi is President & Managing Partner of Investors Pathway LLC — a private equity, capital advisory, and business development firm supporting founders, executives, and institutional investors."
        path="/leadership"
        image={executive.headshot}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="pt-28 md:pt-36 pb-16 md:pb-24"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Leadership", to: "/leadership" },
              { label: "Adeshi Moffi" },
            ]}
          />

          <div className="mt-10 md:mt-16 grid grid-cols-12 gap-8 md:gap-12 items-end">
            <Reveal className="col-span-12 md:col-span-7">
              <div className="section-num">01 / Office of the President</div>
              <h1
                data-testid="executive-name"
                className="h-display text-[3.4rem] sm:text-[4.2rem] md:text-[5.6rem] lg:text-[6.4rem] text-[#0A1128] mt-6"
              >
                Adeshi<br />Moffi.
              </h1>
              <p
                data-testid="executive-title"
                className="mt-6 body-lead max-w-xl text-[#334155]"
              >
                <span className="text-[#0A1128] font-normal">{executive.title}</span>,{" "}
                {executive.company}.
              </p>
              <p className="mt-4 h-editorial text-xl md:text-2xl text-[#0A1128] max-w-xl leading-snug">
                Business Development, Strategic Advisory <span className="text-[#8E7331]">&amp;</span> Capital Readiness.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/leadership/biography" data-testid="cta-read-biography" className="btn-ink">
                  Read the Biography <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
                <Link to="/leadership/contact" data-testid="cta-schedule-consultation" className="btn-outline">
                  Schedule a Consultation
                </Link>
              </div>
            </Reveal>

            <Reveal className="col-span-12 md:col-span-5">
              <div className="relative">
                <div className="absolute -left-3 -top-3 w-16 h-16 border-l border-t border-[#0A1128] hidden md:block" aria-hidden />
                <div className="absolute -right-3 -bottom-3 w-16 h-16 border-r border-b border-[#B89947] hidden md:block" aria-hidden />
                <img
                  src={executive.headshot}
                  alt={executive.headshotAlt}
                  loading="eager"
                  decoding="async"
                  width="1200"
                  height="1500"
                  data-testid="executive-headshot"
                  className="w-full h-[520px] md:h-[640px] object-cover duotone-portrait"
                />
                <div className="mt-4 flex justify-between text-xs font-sans tracking-[0.2em] uppercase text-[#475569]">
                  <span>Executive Portrait</span>
                  <span className="text-[#8E7331]">01 / 01</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="philosophy-section" className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="section-num">02 / Leadership Philosophy</div>
            <div className="mt-6 h-editorial text-2xl md:text-3xl text-[#0A1128] max-w-xs">
              A discipline, not a slogan.
            </div>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-8">
            <blockquote className="h-editorial text-[2rem] md:text-[2.6rem] leading-[1.15] text-[#0A1128] border-l-2 border-[#B89947] pl-6 md:pl-10">
              “{philosophy.headline}”
            </blockquote>
            <p className="body-lead mt-8 max-w-2xl">{philosophy.body}</p>
          </Reveal>
        </div>
      </section>

      {/* BIO SNIPPET */}
      <section data-testid="bio-snippet" className="py-20 md:py-28 border-t border-[#E2E8F0]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="section-num">03 / Executive Biography</div>
            <h2 className="h-editorial text-3xl md:text-5xl mt-6 text-[#0A1128]">
              Institutional experience.<br />Founder instincts.
            </h2>
            <Link to="/leadership/biography" data-testid="cta-full-biography" className="btn-outline mt-10">
              Full Biography <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-8">
            <p className="body-lead">{executive.bio[0]}</p>
            <p className="body-lead mt-6">{executive.bio[1]}</p>
          </Reveal>
        </div>
      </section>

      {/* EXPERTISE */}
      <section data-testid="expertise-section" className="py-20 md:py-28 border-t border-[#E2E8F0]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal className="col-span-12 md:col-span-7">
              <div className="section-num">04 / Areas of Focus</div>
              <h2 className="h-editorial text-3xl md:text-5xl mt-6 text-[#0A1128] max-w-3xl">
                Business Development, Strategic Advisory <span className="text-[#8E7331]">&amp;</span> Capital Readiness.
              </h2>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-5">
              <p className="body-lead">
                Twelve disciplines organized around three practices — helping founders,
                executives, family offices, institutional allocators, and real estate
                sponsors scale through strategic partnerships and reach funding readiness.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#E2E8F0]">
            {executive.expertise.map((e, i) => (
              <Link
                to="/leadership/expertise"
                key={e}
                data-testid={`expertise-cell-${i}`}
                className="cell-rule px-5 py-8 min-h-[140px] flex flex-col justify-between group hover:bg-[#F5F3EE] transition-colors duration-200"
              >
                <span className="section-num text-[#8E7331]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-editorial text-xl md:text-2xl text-[#0A1128] leading-tight mt-6">
                  {e}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* THOUGHT LEADERSHIP */}
      <section data-testid="thought-leadership-section" className="py-20 md:py-28 border-t border-[#E2E8F0]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal className="col-span-12 md:col-span-8">
              <div className="section-num">05 / Thought Leadership</div>
              <h2 className="h-editorial text-3xl md:text-5xl mt-6 text-[#0A1128] max-w-3xl">
                Perspectives on capital, brands, and enduring enterprise value.
              </h2>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-4 md:text-right">
              <Link to="/leadership/thought-leadership" data-testid="cta-all-articles" className="btn-outline">
                All Articles <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.slice(0, 3).map((a) => (
              <Reveal key={a.slug}>
                <Link
                  to={`/leadership/thought-leadership/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group block border border-[#E2E8F0] bg-white hover:border-[#B89947] transition-colors duration-300"
                >
                  <div className="overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-56 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="eyebrow text-[#8E7331]">{a.category}</div>
                    <h3 className="h-editorial text-xl md:text-2xl text-[#0A1128] mt-4 leading-snug">
                      {a.title}
                    </h3>
                    <p className="mt-4 text-sm text-[#475569] font-sans leading-relaxed line-clamp-3">
                      {a.dek}
                    </p>
                    <div className="mt-6 flex items-center justify-between text-xs uppercase tracking-[0.16em] text-[#475569] font-sans">
                      <span>{new Date(a.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      <span>{a.readTime}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT + DOWNLOAD */}
      <section data-testid="contact-cta-section" className="py-20 md:py-28 border-t border-[#E2E8F0]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8 items-center">
          <Reveal className="col-span-12 md:col-span-7">
            <div className="section-num">06 / Office of the President</div>
            <h2 className="h-editorial text-3xl md:text-5xl mt-6 text-[#0A1128] max-w-2xl">
              Confidential dialogue for founders, executives, and allocators.
            </h2>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-5 flex flex-wrap gap-4 md:justify-end">
            <Link to="/leadership/contact" data-testid="cta-contact-office" className="btn-ink">
              Contact the Office
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="cta-download-profile"
              className="btn-outline"
            >
              Download Profile <Download size={14} strokeWidth={1.5} />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
